import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import { UserWithRole } from '../../../shared/services/userService';
import userService from '../../../shared/services/userService';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import UserFilters from './components/UserFilters';
import UserTable from './components/UserTable';
import Pagination from './components/Pagination';
import UserDetailsModal from './components/modals/UserDetailsModal';
import SanctionModal from './components/modals/SanctionModal';
import { useUserManagement } from './hooks/useUserManagement';

const UsersManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isEmbedded = embedded || new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('embed') === '1';
  const { t } = usePageTranslation('panel', 'UsersManagement');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<number | ''>('');
  const [userType, setUserType] = useState<'all' | 'team' | 'players'>('all');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSanctionModal, setShowSanctionModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());

  // Hooks personnalisés
  const { users, roles, loading, error, totalPages, usersReferents, reload } = useUserManagement({
    searchTerm,
    selectedRole,
    page,
  });

  // Vérifier les permissions
  const hasAccess = currentUser?.roles_id && currentUser.roles_id <= 11;
  
  // Permissions pour les sanctions : Modo (3), Admin (2), CM (1)
  const canSanction = currentUser?.roles_id && currentUser.roles_id <= 11;

  const isTeamMember = (user: UserWithRole): boolean => {
    return user.roles_id >= 1 && user.roles_id <= 16;
  };

  const filteredUsers = users.filter(user => {
    if (userType === 'team') {
      return isTeamMember(user);
    } else if (userType === 'players') {
      return user.roles_id === 18;
    }
    return true;
  });

  // Handlers vides pour les actions non disponibles (pas de gestion de rôles)
  const handleRoleChange = () => {};
  const handleAccessToggle = () => {};
  const handleViewHistory = () => {};
  const handlePanelAccess = () => {};
  const handleReferents = () => {};

  const handleViewDetails = (user: UserWithRole) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setResetPasswordMessage(null);
  };

  const handleResetPassword = async (userId: number) => {
    try {
      setResettingPassword(true);
      setResetPasswordMessage(null);
      const response = await userService.resetUserPassword(userId);
      
      if (response.success) {
        setResetPasswordMessage(response.message || 'Email de réinitialisation envoyé avec succès');
      }
    } catch (err: any) {
      setResetPasswordMessage(err.message || 'Erreur lors de l\'envoi de l\'email de réinitialisation');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleOpenSanction = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowSanctionModal(true);
    }
  };

  const handleSanction = async (type: 'ban_permanent' | 'ban_temporary' | 'mute' | 'unban' | 'unmute', duration?: number, reason?: string) => {
    if (!selectedUser) return;

    try {
      setUpdating(true);
      const response = await userService.updateUserAccess(
        selectedUser.id,
        type === 'unban' || type === 'unmute',
        reason
      );

      if (response.success) {
        setShowSanctionModal(false);
        setSelectedUser(null);
        if (reload) {
          await reload();
        }
      }
    } catch (err: any) {
      setResetPasswordMessage(err.message || 'Erreur lors de l\'application de la sanction');
    } finally {
      setUpdating(false);
    }
  };

  // Charger les utilisateurs en ligne au montage et périodiquement
  useEffect(() => {
    if (!hasAccess) return;
    
    const loadOnlineUsers = async () => {
      try {
        const response = await userService.getOnlineUsers();
        if (response.success && response.data) {
          const onlineIds = new Set(response.data.map(u => u.user_id));
          setOnlineUserIds(onlineIds);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs en ligne:', error);
      }
    };

    loadOnlineUsers();
    const interval = setInterval(loadOnlineUsers, 30000); // Actualiser toutes les 30 secondes
    return () => clearInterval(interval);
  }, [hasAccess]);

  const handleForceLogout = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const confirmMessage = `Êtes-vous sûr de vouloir déconnecter de force ${user.username} ?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setUpdating(true);
      const response = await userService.forceLogoutUser(userId);
      if (response.success) {
        // Mettre à jour la liste des utilisateurs en ligne
        setOnlineUserIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        setResetPasswordMessage(response.message || 'Utilisateur déconnecté avec succès');
        // Mettre à jour immédiatement la liste
        setOnlineUserIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      }
    } catch (err: any) {
      setResetPasswordMessage(err.message || 'Erreur lors de la déconnexion forcée');
    } finally {
      setUpdating(false);
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <div className={!isEmbedded ? 'min-h-screen relative overflow-hidden' : ''}>
      {!isEmbedded && <BackgroundImage />}
      {!isEmbedded && <ParticlesBackground />}
      {!isEmbedded && <Navbar />}
      <div className={!isEmbedded ? 'relative z-10 pt-24 pb-16' : ''}>
        <div className={!isEmbedded ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : ''}>
          {!isEmbedded && (
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-cockpit-text mb-2">
                {t('title')}
              </h1>
              <p className="text-cockpit-text-2 text-sm">{t('subtitle')}</p>
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>{t('backToPanel')}</span>
            </button>
          </div>
          )}

          {/* Filtres */}
          <UserFilters
            searchTerm={searchTerm}
            selectedRole={selectedRole}
            roles={roles}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setPage(1);
            }}
            onRoleChange={(value) => {
              setSelectedRole(value);
              setPage(1);
            }}
          />

          {/* Filtre Équipe/Joueurs */}
          <div className="mb-6 flex flex-wrap gap-3">
            <button
              onClick={() => setUserType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors border ${userType === 'all' ? 'border-cockpit-violet/40 bg-cockpit-violet/20 text-cockpit-violet' : 'border-[#223046] bg-[#0b1220]/80 text-cockpit-text-2 hover:text-cockpit-text'}`}
            >
              {t('filters.allUsers')}
            </button>
            <button
              onClick={() => setUserType('team')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors border ${userType === 'team' ? 'border-[#6366f1]/40 bg-[#6366f1]/20 text-[#c7d2fe]' : 'border-[#223046] bg-[#0b1220]/80 text-cockpit-text-2 hover:text-cockpit-text'}`}
            >
              {t('filters.team')}
            </button>
            <button
              onClick={() => setUserType('players')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors border ${userType === 'players' ? 'border-emerald-500/40 bg-emerald-500/20 text-emerald-200' : 'border-[#223046] bg-[#0b1220]/80 text-cockpit-text-2 hover:text-cockpit-text'}`}
            >
              {t('filters.players')}
            </button>
          </div>

          {error && (
            <div className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          {/* Liste des utilisateurs */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <UserTable
                users={filteredUsers}
                onRoleChange={handleRoleChange}
                onAccessToggle={handleAccessToggle}
                onViewHistory={handleViewHistory}
                onPanelAccess={handlePanelAccess}
                onReferents={handleReferents}
                onViewDetails={handleViewDetails}
                onForceLogout={handleForceLogout}
                currentUserId={currentUser?.id}
                currentUserRoleId={currentUser?.roles_id}
                updating={updating}
                isTeamMember={isTeamMember}
                usersReferents={usersReferents}
                onlineUserIds={onlineUserIds}
                readOnly={true}
              />

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}

          {/* Modal des détails */}
          {showDetailsModal && selectedUser && (
            <UserDetailsModal
              user={selectedUser}
              onClose={() => {
                setShowDetailsModal(false);
                setSelectedUser(null);
                setResetPasswordMessage(null);
              }}
              onResetPassword={handleResetPassword}
              resettingPassword={resettingPassword}
              onSanction={canSanction ? handleOpenSanction : undefined}
              onForceLogout={handleForceLogout}
              currentUserRoleId={currentUser?.roles_id}
              currentUserId={currentUser?.id}
              onlineUserIds={onlineUserIds}
              context="users"
            />
          )}

          {/* Modal de sanction */}
          {showSanctionModal && selectedUser && (
            <SanctionModal
              user={selectedUser}
              updating={updating}
              onSanction={handleSanction}
              onCancel={() => {
                setShowSanctionModal(false);
                setSelectedUser(null);
              }}
              currentUserRoleId={currentUser?.roles_id}
              context="users"
            />
          )}

          {resetPasswordMessage && (
            <div className={`mt-6 rounded-lg px-3 py-2 text-sm border ${
              resetPasswordMessage.includes('succès') || resetPasswordMessage.includes('envoyé')
                ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
                : 'bg-red-500/10 border-red-500/40 text-red-200'
            }`}>
              {resetPasswordMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;

